import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  FOREMAN_PHASE_DISPLAY_LEN,
  ForemanPhaseNameFieldModel,
} from '@ups/xplat/utils';

/**
 * Single-field Viewpoint-style phase entry (12·4·2 with dots and leading spaces).
 * Core logic lives in `@ups/xplat/utils` (`ForemanPhaseNameFieldModel`).
 */
@Component({
  selector: 'ups-foreman-phase-name-input',
  imports: [FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './foreman-phase-name-input.component.html',
})
export class ForemanPhaseNameInputComponent {
  private readonly model = new ForemanPhaseNameFieldModel();

  /*
   * `@Input` / `@Output` / `@ViewChild` setters into private signals, not `input()` /
   * `output()` / `viewChild()`: those cannot be bound or registered in this workspace's
   * Vitest JIT (conventions.md, "OnPush and zoneless"). The effects below still track
   * them because each one reads the backing signal.
   */
  private readonly syncKeyState = signal<number | string>(0);
  private readonly prefillRawState = signal<string | null>(null);
  private readonly inputDisabledState = signal(false);
  private readonly phaseInputEl = signal<
    ElementRef<HTMLInputElement> | undefined
  >(undefined);

  /** Increment or change when the owning UI resets the field (e.g. modal open, foreman row id). */
  @Input()
  set syncKey(value: number | string) {
    this.syncKeyState.set(value);
  }
  get syncKey(): number | string {
    return this.syncKeyState();
  }

  /** When set, segments are loaded from this raw phase name after each reset. */
  @Input()
  set prefillRaw(value: string | null) {
    this.prefillRawState.set(value);
  }
  get prefillRaw(): string | null {
    return this.prefillRawState();
  }

  @Input()
  set inputDisabled(value: boolean) {
    this.inputDisabledState.set(value);
  }
  get inputDisabled(): boolean {
    return this.inputDisabledState();
  }

  @Output() formattedValueChange = new EventEmitter<string>();

  @ViewChild('phaseInput')
  set phaseInputRef(ref: ElementRef<HTMLInputElement> | undefined) {
    this.phaseInputEl.set(ref);
  }

  /** Bumps on model changes so computed + DOM effect re-run. */
  private readonly version = signal(0);

  readonly displayLen = FOREMAN_PHASE_DISPLAY_LEN;

  readonly formattedPreview = computed(() => {
    this.version();
    return this.model.formattedForSave();
  });

  constructor() {
    effect(() => {
      this.syncKeyState();
      const raw = this.prefillRawState();
      this.model.reset();
      if (raw != null && String(raw).trim().length > 0) {
        this.model.prefillFromRaw(raw);
      }
      this.emitAndBump();
    });

    effect(() => {
      this.version();
      const display = this.model.display;
      const ref = this.phaseInputEl();
      if (!ref) {
        return;
      }
      const el = ref.nativeElement;
      queueMicrotask(() => {
        if (el.value !== display) {
          el.value = display;
        }
        if (document.activeElement === el && !el.disabled) {
          const pos = this.model.caretPosition();
          el.setSelectionRange(pos, pos);
        }
      });
    });
  }

  onFocus(ev: FocusEvent): void {
    const el = ev.target as HTMLInputElement;
    queueMicrotask(() => {
      if (document.activeElement !== el || el.disabled) {
        return;
      }
      const pos = this.model.caretPosition();
      el.setSelectionRange(pos, pos);
    });
  }

  onClick(ev: MouseEvent): void {
    const el = ev.target as HTMLInputElement;
    const pos = el.selectionStart ?? 0;
    this.model.setActiveSegmentFromCaret(pos);
    queueMicrotask(() => {
      if (document.activeElement === el && !el.disabled) {
        const p = this.model.caretPosition();
        el.setSelectionRange(p, p);
      }
    });
  }

  onKeydown(ev: KeyboardEvent): void {
    if (this.inputDisabled) {
      return;
    }

    const el = ev.target as HTMLInputElement;

    if (ev.key === 'Tab' || ev.key === 'Escape') {
      return;
    }

    if (ev.key === '.') {
      ev.preventDefault();
      this.model.advanceSegment();
      this.emitAndBump();
      queueMicrotask(() => {
        if (document.activeElement === el && !el.disabled) {
          const p = this.model.caretPosition();
          el.setSelectionRange(p, p);
        }
      });
      return;
    }

    if (ev.key === 'Backspace') {
      ev.preventDefault();
      this.model.backspace();
      this.emitAndBump();
      return;
    }

    if (ev.key === 'ArrowLeft') {
      ev.preventDefault();
      this.model.goToPreviousSegment();
      this.emitAndBump();
      return;
    }

    if (ev.key === 'ArrowRight') {
      ev.preventDefault();
      this.model.advanceSegment();
      this.emitAndBump();
      return;
    }

    if (ev.key.length === 1 && !ev.ctrlKey && !ev.metaKey && !ev.altKey) {
      if (ev.key === '\n' || ev.key === '\r') {
        return;
      }
      ev.preventDefault();
      this.model.appendChar(ev.key);
      this.emitAndBump();
    }
  }

  onInput(ev: Event): void {
    const el = ev.target as HTMLInputElement;
    const expected = this.model.display;
    if (el.value !== expected) {
      el.value = expected;
      queueMicrotask(() => {
        if (document.activeElement === el && !el.disabled) {
          const pos = this.model.caretPosition();
          el.setSelectionRange(pos, pos);
        }
      });
    }
  }

  onPaste(ev: ClipboardEvent): void {
    if (this.inputDisabled) {
      return;
    }
    ev.preventDefault();
    const text = ev.clipboardData?.getData('text') ?? '';
    this.model.applyPaste(text);
    this.emitAndBump();
  }

  private emitAndBump(): void {
    this.formattedValueChange.emit(this.model.formattedForSave());
    this.version.update((v) => v + 1);
  }
}
